import { Routes, Route, Navigate } from 'react-router-dom';

import {
  INTERNAL_UTILS_ROUTE,
  PORTFOLIO_ROUTE,
  TRADE_ROUTE,
  STATUS_ROUTE,
} from '#/routes';
import * as Logging from '#/features/logging/logging';
import Portfolio from '#/components/portfolio/Portfolio';
import InternalUtils from '#/components/internal-utils/InternalUtils';
import Perpetuals from '#/components/perpetuals/Perpetuals';
import AppBar from '#/components/common/app-bar/AppBar';
import ToastContainer from '#/components/common/toast/ToastContainer';
import WalletModalConnectAndOnboard from '#/components/common/wallet/modal/WalletModalConnectAndOnboard';
import WalletModal from '#/components/common/wallet/modal/WalletModal';
import UsernamePrompter from '#/components/account/UsernamePrompter';
import AuthManager from '#/components/common/auth/AuthManager';
import IdentifyUser from '#/components/common/IdentifyUser';
import NotificationsDrawer from '#/components/common/notifications/NotificationsDrawer';
import AntdThemeProvider from '#/components/common/AntdThemeProvider';
import NotificationsListener from '#/components/common/notifications/NotificationsListener';
import ErrorPage from '#/components/common/ErrorPage';
import { BetaDisclaimerToast } from '#/components/common/notifications/disclaimers';
import Status from '#/components/Status';
import Footer from '#/components/common/Footer';
import DebugMonitor from '#/features/debug/DebugMonitor';

import { AccountProvider } from '#/features/account/account-context';
import { AccountProfileProvider } from '#/features/account/account-profile-context';
import { ConnectOnboardWalletModalProvider } from '#/features/wallet/modal-connect-onboard-context';
import {
  WalletActionsProvider,
  WalletStateProvider,
  WalletViewProvider,
} from '#/features/wallet/wallet-context';
import { GlobalNotifications } from '#/features/notifications/GlobalNotifications';
import { PerpetualMarketsStateProvider } from '#/features/perpetuals/market-info/markets-context';
import { ParadexWebSocketProvider } from '#/features/ws/ws-context';
import {
  AuthActionsProvider,
  AuthStateProvider,
  AuthViewProvider,
} from '#/features/auth/auth-context';
import { WalletModalProvider } from '#/features/wallet/modal-context';
import { TransfersProvider } from '#/features/transfers/transfers-context';
import { AccountSummaryViewProvider } from '#/features/account/account-summary-context';
import { MarginSummaryViewProvider } from '#/features/account/margin-summary-context';
import { OpenPositionsProvider } from '#/features/perpetuals/open-positions-context';
import { OpenOrdersProvider } from '#/features/perpetuals/trade-overview/open-orders-context';
import {
  BalancesProvider,
  BalancesStoreProvider,
} from '#/features/balance/balance-context';
import { OrdersProvider } from '#/features/perpetuals/trade-overview/orders-context';
import { FillsProvider } from '#/features/perpetuals/trade-overview/fills-context';
import { FundingPaymentsProvider } from '#/features/perpetuals/trade-overview/funding-payments-context';
import { MarketsSummaryStateProvider } from '#/features/perpetuals/market-info/markets-summary-context';
import { NotificationsDrawerProvider } from '#/features/notifications/drawer-context';
import { NotificationsProvider } from '#/features/notifications/notifications-context';
import { UserPreferencesProvider } from '#/features/user-preferences/user-preferences-context';
import {
  SystemConfigProvider,
  useSystemConfigStatusView,
} from '#/features/system/system-config-context';
import { WalletConnectConfigProvider } from '#/features/wallet/wallet-connect-context';
import { LastVisitedSymbolProvider } from '#/features/perpetuals/last-visited-symbol-context';
import { UsernamePromptModalProvider } from '#/features/account/username-prompt-modal-context';
import { LocalizationProvider } from '#/features/localization/localization-context';
import { FeatureFlags } from './features/feature-flags';
import { ScreenSizeProvider } from './features/layout/screen-size-context';

Logging.setup();

export default function App() {
  return (
    <SystemConfigProvider>
      <AppWithConfig />
    </SystemConfigProvider>
  );
}

function AppWithConfig() {
  const configStatus = useSystemConfigStatusView();

  if (configStatus === 'loading') {
    // Empty page to increase perceived loading time
    return null;
  }

  if (configStatus === 'error') {
    return (
      <ErrorPage>
        The app failed to load.
        <br />
        Please refresh the page.
      </ErrorPage>
    );
  }

  return (
    <LocalizationProvider>
      <ScreenSizeProvider>
        <AntdThemeProvider>
          <WalletConnectConfigProvider>
            <WalletStateProvider>
              <IdentifyUser />
              <AuthStateProvider>
                <AuthViewProvider>
                  <WalletViewProvider>
                    <UserPreferencesProvider>
                      <ParadexWebSocketProvider>
                        <AuthActionsProvider>
                          <WalletActionsProvider>
                            <AuthManager />
                            <AccountProvider>
                              <AccountProfileProvider>
                                <PerpetualMarketsStateProvider>
                                  <ConnectOnboardWalletModalProvider>
                                    <WalletModalProvider>
                                      <UsernamePromptModalProvider>
                                        <TransfersProvider>
                                          <MarketsSummaryStateProvider>
                                            <OpenPositionsProvider>
                                              <OpenOrdersProvider>
                                                <BalancesStoreProvider>
                                                  <BalancesProvider>
                                                    <OrdersProvider>
                                                      <FillsProvider>
                                                        <FundingPaymentsProvider>
                                                          <AccountSummaryViewProvider>
                                                            <MarginSummaryViewProvider>
                                                              <NotificationsDrawerProvider>
                                                                <NotificationsProvider>
                                                                  <NotificationsListener />
                                                                  <GlobalNotifications />
                                                                  <AppContent />
                                                                </NotificationsProvider>
                                                              </NotificationsDrawerProvider>
                                                            </MarginSummaryViewProvider>
                                                          </AccountSummaryViewProvider>
                                                        </FundingPaymentsProvider>
                                                      </FillsProvider>
                                                    </OrdersProvider>
                                                  </BalancesProvider>
                                                </BalancesStoreProvider>
                                              </OpenOrdersProvider>
                                            </OpenPositionsProvider>
                                          </MarketsSummaryStateProvider>
                                        </TransfersProvider>
                                      </UsernamePromptModalProvider>
                                    </WalletModalProvider>
                                  </ConnectOnboardWalletModalProvider>
                                </PerpetualMarketsStateProvider>
                              </AccountProfileProvider>
                            </AccountProvider>
                          </WalletActionsProvider>
                        </AuthActionsProvider>
                      </ParadexWebSocketProvider>
                    </UserPreferencesProvider>
                  </WalletViewProvider>
                </AuthViewProvider>
              </AuthStateProvider>
            </WalletStateProvider>
          </WalletConnectConfigProvider>
        </AntdThemeProvider>
      </ScreenSizeProvider>
    </LocalizationProvider>
  );
}

function AppContent() {
  const isAccountUsernamesEnabled = FeatureFlags.getBooleanValue(
    'account-usernames-enabled',
    false,
  );
  return (
    <>
      <DebugMonitor />
      <ToastContainer />
      <BetaDisclaimerToast />
      <AppBar />
      <NotificationsDrawer />
      <WalletModalConnectAndOnboard />
      <WalletModal />
      {isAccountUsernamesEnabled && <UsernamePrompter />}
      <LastVisitedSymbolProvider>
        <Routes>{routes()}</Routes>
      </LastVisitedSymbolProvider>
      <Footer />
    </>
  );
}

function routes() {
  switch (INTERNAL_UTILS) {
    case 'enable':
      return [internalUtilsRoutes, appRoutes, appRedirect];
    case 'exclusive':
      return [internalUtilsRoutes, internalUtilsRedirect];
    default:
      return [appRoutes, appRedirect];
  }
}

const appRoutes = [
  <Route
    path={`${TRADE_ROUTE}/*`}
    key={TRADE_ROUTE}
    element={<Perpetuals />}
  />,
  <Route
    path={`${PORTFOLIO_ROUTE}/*`}
    key={PORTFOLIO_ROUTE}
    element={<Portfolio />}
  />,
  <Route path={`${STATUS_ROUTE}/*`} key={STATUS_ROUTE} element={<Status />} />,
];

const appRedirect = (
  <Route
    path="*"
    key={`redirect-${TRADE_ROUTE}`}
    element={<Navigate to={TRADE_ROUTE} replace />}
  />
);

const internalUtilsRoutes = [
  <Route
    path={`${INTERNAL_UTILS_ROUTE}/*`}
    key={INTERNAL_UTILS_ROUTE}
    element={<InternalUtils />}
  />,
];

const internalUtilsRedirect = (
  <Route
    path="*"
    key={`redirect-${INTERNAL_UTILS_ROUTE}`}
    element={<Navigate to={INTERNAL_UTILS_ROUTE} replace />}
  />
);
